/**
 * Conversión de montos entre monedas. Todo se calcula contra tasas BASE-USD:
 *   rates[code] = unidades de `code` por 1 USD   (rates.USD === 1)
 *
 *   convert(amount, from, to) = (amount / rates[from]) * rates[to]
 *
 * Las tasas viven en memoria en este módulo (única fuente). `exchangeRate.js`
 * las refresca vía setRates(); arrancamos desde el cache de localStorage o, en
 * su defecto, desde un fallback hardcodeado para nunca quedar sin tasa.
 */

import { getActiveSheet } from '../sheetsStore.js';
import {
  getDisplayCurrency,
  getSheetCurrency,
  getCachedRates,
} from '../currencyStore.js';

// Fallback razonable USD→MXN por si no hay red ni cache (se sobrescribe al
// llegar las tasas reales).
export const FALLBACK_RATES = { USD: 1, MXN: 18.5 };

let rates = { ...FALLBACK_RATES };

// Hidratamos desde el cache al cargar el módulo (si existe).
(function hydrate() {
  const cached = getCachedRates();
  if (cached && cached.rates) setRates(cached.rates);
})();

/** Reemplaza las tasas en memoria, garantizando USD=1 y MXN presente. */
export function setRates(next) {
  if (!next || typeof next !== 'object') return;
  rates = { ...FALLBACK_RATES, ...next, USD: 1 };
}

export function getRates() {
  return rates;
}

/** Tasa USD→MXN actual (para etiquetas tipo "1 USD = $X MXN"). */
export function usdToMxn() {
  return rates.MXN || FALLBACK_RATES.MXN;
}

export function convert(amount, from, to) {
  const a = Number(amount) || 0;
  if (!from || !to || from === to) return a;
  const rf = rates[from];
  const rt = rates[to];
  if (!rf || !rt) return a; // moneda desconocida → no convertimos
  return (a / rf) * rt;
}

/**
 * Moneda nativa de una fila de venta:
 *   - En "Todas las hojas" cada fila trae SHEET_NAME → usamos esa.
 *   - En hoja única, las filas no traen SHEET_NAME → usamos la hoja activa.
 *   - Hoja por defecto / desconocida → MXN.
 */
export function nativeCurrencyOf(row) {
  const name = (row && row.SHEET_NAME) || getActiveSheet();
  return getSheetCurrency(name);
}

/** Monto de una fila convertido a la moneda de display actual. */
export function montoDisplay(row) {
  return convert(
    (row && row.MONTO) || 0,
    nativeCurrencyOf(row),
    getDisplayCurrency()
  );
}

/** Convierte un monto en MXN (p.ej. costos) a la moneda de display. */
export function convertFromMXN(amount) {
  return convert(amount, 'MXN', getDisplayCurrency());
}
